import UtilService from '@/services/utilService.js'
import forecastingProfileApi from '@/api/forecastingProfileApi'

export default {
    namespaced: true,
    state: {
        varietySummaries: [],
        plants: [],
        divisions: [],
        croppingSystems: [],
        plantAges: [],
        forecastProfiles: [],
        forecasts: []
    },
    getters: {
        getPlants(state){
            return state.plants;
        },
        getDivisions(state){
            return state.divisions;
        },
        getVarietySummaries(state){
            return state.varietySummaries;
        },
        getCroppingSystems(state) {
            return state.croppingSystems;
        },
        getPlantAges(state) {
            return state.plantAges;
        },
        getForecasts(state) {
            return (query) => {
                let items = state.forecasts;

                // Filter
                items = items.filter(
                    x => (x.CropLocationID == query.cropLocationID)                    
                );

                return items;
            }
        },
        getForecastProfiles(state){
            return (query) => {
                let items = state.forecastProfiles;

                // Filter
                items = items.filter(
                    x => (x.CropLocationID == query.cropLocationID)                    
                );

                // Map
                items = items
                    .map(x => {
                        let item = {
                            BerriesForecastProfileID: x.BerriesForecastProfileID,
                            TemplateBerriesForecastProfileID: x.TemplateBerriesForecastProfileID,
                            Variety: {
                                VarietyID: x.VarietyID,
                                VarietyName: null
                            },
                            Plant: {
                                PlantID: x.PlantID,
                                PlantName: null
                            },
                            Division: {
                                DivisionID: x.DivisionID,
                                DivisionName: null
                            },
                            CroppingSystem: {
                                BerriesCroppingSystemID: x.BerriesCroppingSystemID,
                                Description: null
                            },
                            PlantAge: {
                                BerriesPlantAgeID: x.BerriesPlantAgeID,
                                Description: null
                            },                            
                            StartWeek: x.StartWeek,
                            TonnesPerHectare: x.TonnesPerHectare,
                            CropLocationID: x.CropLocationID,
                            StartYear: x.StartYear,
                            IsActive: x.IsActive,
                            CropArea: x.CropArea,
                            BudgetValues: x.BudgetValues.map(y => {
                                return {
                                    BerriesForecastProfileValueID: y.BerriesForecastProfileValueID,
                                    BerriesForecastProfileWeekNumber: y.BerriesForecastProfileWeekNumber,
                                    BudgetValue: y.BudgetValue
                                };
                            })
                        };

                        let varietySummary = state.varietySummaries.find(y => y.VarietyID == x.VarietyID);
                        if (varietySummary != null) {
                            item.Variety.VarietyName = varietySummary.VarietyName;
                            item.Variety.VarietyDescription = varietySummary.VarietyDescription;
                        }

                        let plant = state.plants.find(p => p.PlantID == x.PlantID);
                        if (plant != null) {
                            item.Plant.PlantName = plant.PlantName;
                        }

                        let division = state.divisions.find(d => d.DivisionID == x.DivisionID);
                        if (division != null) {
                            item.Division.DivisionName = division.DivisionName;
                        }

                        let croppingSystem = state.croppingSystems.find(c => c.BerriesCroppingSystemID == x.BerriesCroppingSystemID);
                        if (croppingSystem != null) {
                            item.CroppingSystem.Description = croppingSystem.Description;
                        }

                        let plantAge = state.plantAges.find(c => c.BerriesPlantAgeID == x.BerriesPlantAgeID);
                        if (plantAge != null) {
                            item.PlantAge.Description = plantAge.Description;
                        }

                        item.IsArchived = x.IsArchived;
                        return item;
                    })
                    .filter(x => !x.ShouldDelete);

                // Sort
                items = items.sort(UtilService.sortAscending(y => y.Division.DivisionName));

                return items;
            };
        },
    },
    mutations: {
        updateForecastProfiles(state, value) {
            state.forecastProfiles.splice(0, state.forecastProfiles.length, ...value);
        },
        addRemoveForecastProfile(state, value) {
            let index = state.forecastProfiles.findIndex(t => t.BerriesForecastProfileID === value.BerriesForecastProfileID);
            
            if (index > -1) {
                if (value.ShouldDelete) {
                    state.forecastProfiles.splice(index, 1);
                } else {
                    state.forecastProfiles.splice(index, 1, value);
                }
            } else {
                state.forecastProfiles.push(value);
            }
        },
        toggleArchiveForecastProfile(state, value) {
            let index = state.forecastProfiles.findIndex(t => t.BerriesForecastProfileID === value);
            if (index > -1) {
                state.forecastProfiles[index].IsArchived = !state.forecastProfiles[index].IsArchived;
            }
        },
        updateVarietySummaries(state, value) {
            state.varietySummaries.splice(0, state.varietySummaries.length, ...value);
        },
        updatePlants(state, value) {
            state.plants.splice(0, state.plants.length, ...value);
        },
        updateDivisions(state, value) {
            state.divisions.splice(0, state.divisions.length, ...value);
        },
        updateCroppingSystems(state, value) {
            state.croppingSystems.splice(0, state.croppingSystems.length, ...value);
        },
        updatePlantAges(state, value) {
            state.plantAges.splice(0, state.plantAges.length, ...value);
        },
        updateForecasts(state, value) {
            value.forEach(forecast => {
                let index = state.forecasts.findIndex(x => x.CropLocationID == forecast.CropLocationID && x.VarietyID == forecast.VarietyID && x.ForecastDate == forecast.ForecastDate);
                if(index >= 0){
                    state.forecasts.splice(index, 1, forecast);
                } else {
                    state.forecasts.push(forecast);
                } 
            });
        },
    },
    actions: {
        async loadPlantProfileTemplatesAsync({ commit }, options){
            let forecastProfileResponse = await forecastingProfileApi.getPlantProfileTemplates(options.plantID);
            
            commit('updateForecastProfiles', forecastProfileResponse.Profiles);            
            commit('updateVarietySummaries', forecastProfileResponse.VarietySummaries);
            commit('updatePlants', forecastProfileResponse.Plants);
            commit('updateDivisions', forecastProfileResponse.Divisions);
            commit('updateCroppingSystems', forecastProfileResponse.CroppingSystems);
            commit('updatePlantAges', forecastProfileResponse.PlantAges);
        },
        async loadCropForecastProfilesAsync({ commit }, options){
            let cropResponse = await forecastingProfileApi.getCropForecastProfiles(options.cropLocationID);
            let plantTemplateResponse = await forecastingProfileApi.getPlantProfileTemplatesForCrop(options.cropLocationID);

            let combinedProfiles = cropResponse.Profiles.concat(plantTemplateResponse.Profiles);

            commit('updateForecastProfiles', combinedProfiles);            
            commit('updateVarietySummaries', plantTemplateResponse.VarietySummaries);
            commit('updatePlants', plantTemplateResponse.Plants);
            commit('updateDivisions', plantTemplateResponse.Divisions);
            commit('updateCroppingSystems', plantTemplateResponse.CroppingSystems);
            commit('updatePlantAges', plantTemplateResponse.PlantAges);
        },
        async loadCropForecastsAsync({ commit }, options) {
            let forecasts = await forecastingProfileApi.getCropForecasts(options.cropLocationID, options.varietyID, options.startDate, options.endDate);

            commit('updateForecasts', forecasts);
        },
        async updateForecastProfile({ commit }, params){
            // Send new data to server
            let forecastProfileResponse = await forecastingProfileApi.postUpdateForecastProfile(params)
            
            commit('addRemoveForecastProfile', forecastProfileResponse.Profiles[0] || params);
        },
        async toggleArchiveForecastProfile({ commit }, params){
            await forecastingProfileApi.postToggleArchiveForecastProfile(params.BerriesForecastProfileID)
            
            commit('toggleArchiveForecastProfile', params.BerriesForecastProfileID);
        },
        async downloadPlantProfileTemplateData({ commit }, params){
            return await forecastingProfileApi.downloadPlantProfileTemplateData(params.ProfileTemplateIDs)
        },
        async uploadProfileTemplates({ commit }, payload) {
            return await forecastingProfileApi.uploadProfileTemplates(payload);
        }
    }
}